import {
  HSE_PRIORITIES,
  HSE_REPORT_TYPES,
  HSEQ_STAGES,
  type HseqStage,
  type HseReportType,
  hseTypeCode,
  isHseReportType,
} from '@felix/domain';
import { Button, Select, TextInput } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCalendar,
  IconCircleCheck,
  IconClipboardList,
  IconClock,
  IconHourglass,
  IconPlus,
  IconSearch,
  IconSend,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatTile } from '../../StatTile.js';
import table from '../materials/Materials.module.css';
import {
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from '../purchasing/table.js';
import classes from './Hse.module.css';
import { HseLoadError, HsePriorityBadge, HseqStageBadge } from './parts.js';
import { filterHse, type HseColumn, type HseFilter, NO_HSE_FILTER, sortHse } from './routes.js';
import type { HseSource } from './source.js';
import type { HseListResult, HseOfficeRequest } from './types.js';

const PAGE_SIZE = 10;

type State =
  | { kind: 'loading' }
  | { kind: 'error'; error: unknown }
  | { kind: 'ready'; list: HseListResult };

const COLUMNS: readonly HseColumn[] = [
  'reportNo',
  'type',
  'date',
  'vessel',
  'category',
  'description',
  'status',
  'priority',
  'responsible',
];

/**
 * HSEQ listesi (gemi-hse-genel.jpeg dili; ADR 0049 §11, ADR 0097 §1): başlık, tarih aralığı,
 * "New Report" (tür seçimi), özet kartları (Total, Open, Pending Review, Overdue, Closed), tür
 * düğmeleri (All / NC / DF / NM / ACC), arama, aşama ve öncelik süzgeci; ID / Type / Date / Vessel
 * / Category / Description / Stage / Priority / Responsible tablosu ve sayfalama. Türün eski
 * yolunda o tür seçili açılır. Gemide o gemi, ofiste filo.
 */
export function HseList({
  source,
  type,
  onOpen,
  onNew,
  onRequest,
  segment,
}: {
  source: HseSource;
  /** Ofiste Non Conformity: "Vessel reports | Company (office)" seçimi (ADR 0062 §2). */
  segment?: React.ReactNode;
  /** Türün eski yolunda başlangıç süzgeci; `null`: dört tür. */
  type: HseReportType | null;
  onOpen: (id: string) => void;
  /** Ofis talebinden açılıyorsa talep ve türü. */
  onNew: (request?: { id: string; type: HseReportType } | null) => void;
  /** Ofiste rapor açma talebi penceresi. */
  onRequest?: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) => t(`hse.list.${key}`, options);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [filter, setFilter] = useState<HseFilter>(() => ({
    ...NO_HSE_FILTER,
    types: type ? [type] : [],
  }));
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState<HseColumn>>({ key: 'date', dir: 'desc' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      setState({ kind: 'ready', list: await source.list(null) });
    } catch (error) {
      setState({ kind: 'error', error });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const ready = state.kind === 'ready' ? state.list : null;
  const items = useMemo(
    () => (ready ? sortHse(filterHse(ready.items, filter), sort.key, sort.dir) : []),
    [ready, filter, sort],
  );
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const change = (next: Partial<HseFilter>) => {
    setFilter((f) => ({ ...f, ...next }));
    setPage(1);
  };
  const typeName = type ? t(`hse.types.${type}`) : t('hse.hseq.title');
  const ship = source.scope === 'ship';
  const canCreate = Boolean(ship && ready?.canCreate && source.editor);
  const kpis = ready?.kpis;
  const tile = (
    key: 'total' | 'open' | 'pendingReview' | 'overdue' | 'closed',
    status: HseFilter['status'],
    icon: React.ReactNode,
    tone: 'default' | 'success' | 'warning' | 'danger',
  ) => (
    <button
      type="button"
      className={classes.tileButton}
      aria-pressed={filter.status === status && status !== null}
      onClick={() => change({ status: filter.status === status ? null : status })}
    >
      <StatTile icon={icon} label={k(`kpi.${key}`)} value={kpis ? kpis[key] : '—'} tone={tone} />
    </button>
  );

  return (
    <div className={classes.screen}>
      <PageHeader
        title={t('hse.hseq.title')}
        description={t('hse.hseq.description')}
        actions={
          <div className={classes.headerActions}>
            <fieldset className={classes.range} aria-label={k('dateRange')}>
              <IconCalendar size={16} aria-hidden />
              <DayInput
                variant="unstyled"
                size="xs"
                rightSection={null}
                aria-label={k('dateFrom')}
                placeholder={k('dateFrom')}
                value={filter.from}
                maxDate={filter.to ?? undefined}
                onChange={(value) => change({ from: value })}
              />
              <span aria-hidden>–</span>
              <DayInput
                variant="unstyled"
                size="xs"
                rightSection={null}
                aria-label={k('dateTo')}
                placeholder={k('dateTo')}
                value={filter.to}
                minDate={filter.from ?? undefined}
                onChange={(value) => change({ to: value })}
              />
            </fieldset>
            {canCreate ? (
              <Button leftSection={<IconPlus size={16} />} onClick={() => onNew()}>
                {type ? t(`hse.newTitle.${type}`) : t('hse.hseq.newReport')}
              </Button>
            ) : null}
            {!ship && ready?.canRequest && onRequest ? (
              <Button variant="default" leftSection={<IconSend size={16} />} onClick={onRequest}>
                {k('requestReport')}
              </Button>
            ) : null}
          </div>
        }
      />

      {segment ?? null}

      {ship && (ready?.officeRequests?.length ?? 0) > 0 ? (
        <OfficeRequests
          requests={ready?.officeRequests ?? []}
          type={type}
          canCreate={canCreate}
          onOpen={(request) => onNew(request)}
        />
      ) : null}

      <div className={classes.tiles}>
        {tile('total', null, <IconClipboardList size={20} />, 'default')}
        {tile('open', 'open', <IconAlertTriangle size={20} />, 'danger')}
        {tile('pendingReview', 'pending_review', <IconHourglass size={20} />, 'warning')}
        {tile('overdue', 'overdue', <IconClock size={20} />, 'danger')}
        {tile('closed', 'closed', <IconCircleCheck size={20} />, 'success')}
      </div>

      <Panel flush aria-label={k('table', { type: typeName })}>
        <fieldset className={classes.chips} aria-label={t('hse.hseq.typeFilter')}>
          {[null, ...HSE_REPORT_TYPES].map((chip) => {
            const pressed = chip === null ? filter.types.length === 0 : filter.types.includes(chip);
            return (
              <Button
                key={chip ?? 'all'}
                size="xs"
                radius="xl"
                variant={pressed ? 'filled' : 'default'}
                aria-pressed={pressed}
                title={chip ? t(`hse.hseq.typeNames.${chip}`) : undefined}
                onClick={() => change({ types: chip ? [chip] : [] })}
              >
                {chip
                  ? `${hseTypeCode(chip)} · ${t(`hse.hseq.typeNames.${chip}`)}`
                  : t('hse.hseq.allTypes')}
              </Button>
            );
          })}
        </fieldset>
        <form
          className={classes.toolbar}
          aria-label={k('filters')}
          onSubmit={(event) => event.preventDefault()}
        >
          <TextInput
            label={k('search')}
            placeholder={k('searchPlaceholder', { type: typeName.toLocaleLowerCase() })}
            leftSection={<IconSearch size={16} aria-hidden />}
            value={filter.search}
            onChange={(event) => change({ search: event.currentTarget.value })}
          />
          <Select
            label={t('hse.hseq.stage')}
            data={[
              { value: '', label: t('hse.hseq.allStages') },
              ...HSEQ_STAGES.map((value) => ({ value, label: t(`hse.hseq.stages.${value}`) })),
            ]}
            value={filter.stage ?? ''}
            allowDeselect={false}
            onChange={(value) => change({ stage: (value || null) as HseqStage | null })}
          />
          <Select
            label={k('priority')}
            data={[
              { value: '', label: k('allPriority') },
              ...HSE_PRIORITIES.map((value) => ({ value, label: t(`hse.priorities.${value}`) })),
            ]}
            value={filter.priority ?? ''}
            allowDeselect={false}
            onChange={(value) => change({ priority: value || null })}
          />
          {!ship ? (
            <Select
              label={k('vessel')}
              data={[
                { value: '', label: k('allVessels') },
                ...(ready?.vessels ?? []).map((v) => ({ value: v.id, label: v.name })),
              ]}
              value={filter.vesselId ?? ''}
              allowDeselect={false}
              onChange={(value) => change({ vesselId: value || null })}
            />
          ) : null}
        </form>
        {state.kind === 'error' ? (
          <HseLoadError error={state.error} onRetry={() => void load()} />
        ) : state.kind === 'loading' ? (
          <TableSkeleton columns={9} label={k('table', { type: typeName })} />
        ) : (ready?.items.length ?? 0) === 0 ? (
          <EmptyState
            title={k('emptyTitle', { type: typeName })}
            description={k(ship ? 'emptyShip' : 'emptyWeb')}
          />
        ) : items.length === 0 ? (
          <NoMatch title={k('noMatch')} onClear={() => change(NO_HSE_FILTER)} />
        ) : (
          <>
            <section
              className={table.tableWrap}
              aria-label={k('table', { type: typeName })}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${classes.table}`}>
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <SortableTh
                        key={column}
                        column={column}
                        label={k(`columns.${column}`)}
                        sort={sort}
                        onSort={setSort}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((item) => (
                    <tr key={item.id}>
                      <td className={classes.key}>
                        <a
                          className={classes.link}
                          href={`?id=${item.id}`}
                          aria-label={k('open', { no: item.reportNo })}
                          onClick={(event) => {
                            event.preventDefault();
                            onOpen(item.id);
                          }}
                        >
                          {item.reportNo}
                        </a>
                      </td>
                      <td title={t(`hse.hseq.typeNames.${item.reportType}`)}>
                        {hseTypeCode(item.reportType)}
                      </td>
                      <td className={classes.nowrap}>
                        {formatDay(item.localDate ?? item.createdAt.slice(0, 10))}
                      </td>
                      <td>{item.vesselName}</td>
                      <td>
                        {item.category
                          ? t(`hse.categories.${item.reportType}.${item.category}`, {
                              defaultValue: item.category,
                            })
                          : '—'}
                      </td>
                      <td className={classes.wrap} title={item.description ?? undefined}>
                        {item.title}
                      </td>
                      <td>
                        <HseqStageBadge status={item.status} />
                        <div className={classes.muted}>{t(`hse.statuses.${item.status}`)}</div>
                        {item.overdue ? (
                          <span className={classes.overdue}>
                            <HseOverdueMark />
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <HsePriorityBadge priority={item.priority} />
                      </td>
                      <td>{item.responsible ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={current}
              pages={pages}
              summary={k('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, items.length),
                total: items.length,
              })}
              label={k('pagination')}
              onPage={setPage}
            />
          </>
        )}
      </Panel>
    </div>
  );
}

function HseOverdueMark() {
  const { t } = useTranslation();
  return (
    <span className={classes.muted} title={t('hse.list.overdue')}>
      <IconClock size={14} aria-hidden /> {t('hse.list.overdue')}
    </span>
  );
}

/** Ofisin rapor açma talepleri (ADR 0049 §7): gemi talepten taslak açar. */
function OfficeRequests({
  requests,
  type,
  canCreate,
  onOpen,
}: {
  requests: readonly HseOfficeRequest[];
  type: HseReportType | null;
  canCreate: boolean;
  onOpen: (request: { id: string; type: HseReportType }) => void;
}) {
  const { t } = useTranslation();
  const own = requests.filter(
    (r) => isHseReportType(r.requestedType) && (type === null || r.requestedType === type),
  );
  if (own.length === 0) return null;
  return (
    <Panel title={t('hse.list.officeRequests.title', { count: own.length })}>
      <ul className={classes.requests}>
        {own.map((request) => (
          <li key={request.id} className={classes.request}>
            <div>
              <div className={classes.requestTitle}>{request.title}</div>
              <div className={classes.muted}>{request.body}</div>
            </div>
            {canCreate ? (
              <Button
                size="xs"
                onClick={() =>
                  onOpen({ id: request.id, type: request.requestedType as HseReportType })
                }
              >
                {t('hse.list.officeRequests.open')}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </Panel>
  );
}
